import type { Content, Root, Text } from "mdast";
import { MutableRefObject } from "react";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

const CITATION_REGEX = /\(?\[citation:\s*([0-9,\s]+)\]\)?/g;

interface ReplaceCitationsOptions {
    citationMap: Map<number, number>;
    nextNumberRef: MutableRefObject<number>;
}

export const replaceCitations: Plugin<[ReplaceCitationsOptions], Root> = ({
    citationMap,
    nextNumberRef,
}) => {
    return (tree) => {
        visit(tree, "text", (node: Text, index, parent) => {
            if (!parent || typeof node.value !== "string") return;

            const parts = node.value.split(CITATION_REGEX);
            if (parts.length < 2) return;

            const newNodes: Content[] = [];
            for (let i = 0; i < parts.length; i++) {
                // An even index is regular text
                if (i % 2 === 0) {
                    if (parts[i]) {
                        newNodes.push({
                            type: "text",
                            value: parts[i],
                        } satisfies Text);
                    }
                } else {
                    // Parse citation indices - handle multiple comma-separated values
                    const citationIndicesStr = parts[i].trim();
                    const citationIndices = citationIndicesStr
                        .split(",")
                        .map((s) => parseInt(s.trim()))
                        .filter((n) => !isNaN(n));

                    // Create citation nodes for each index
                    citationIndices.forEach((citationIndex, idx) => {
                        if (!citationMap.has(citationIndex)) {
                            citationMap.set(
                                citationIndex,
                                nextNumberRef.current++
                            );
                        }

                        const displayNumber = citationMap.get(citationIndex)!;

                        const citationNode = {
                            type: "element",
                            tagName: "citation",
                            data: {
                                hName: "citation",
                                hProperties: {
                                    citationIndex,
                                    displayNumber,
                                },
                            },
                            children: [],
                        };
                        newNodes.push(citationNode as Content);
                    });
                }
            }

            // Replace the old node with newNodes
            parent.children.splice(index!, 1, ...newNodes);
        });
    };
};
